import { ApiClient } from '../utils/apiClient';
import { destroyMqttAudioManager } from './audio/MqttAudioManager';
import { destroyMqttManager } from './connections/MqttManager';
import { logoutCleanupService } from './logoutCleanupService';
import { logger } from '../utils/logger';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  timestamp: number;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
  user: User;
}

export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  phoneCountryCode?: number;
  phoneNumber?: string;
  organization?: string;
  callSign?: string;
}

export interface User {
  id: number; // 与后端一致，使用number类型
  username: string;
  email?: string; // email字段可以为空
  phoneNumber?: string;
  avatarUrl?: string;
  phoneCountryCodeId?: number;
  callSign?: string;
}

class AuthService {
  /**
   * 将登录技术错误消息转换为用户友好的消息
   * 生产环境不泄露系统内部信息
   */
  private getLoginFriendlyErrorMessage(error: any): string {
    const isProduction = process.env.NODE_ENV === 'production';
    const errorMessage = error instanceof Error ? error.message : String(error);
    
    // 在生产环境中，记录详细错误但不返回给用户
    if (isProduction) {
      logger.error('登录错误详情（仅记录）:', {
        message: errorMessage,
        type: error?.constructor?.name,
        // 不记录堆栈跟踪和敏感信息
      });
    }
    
    // 网络错误
    if (errorMessage.includes('NetworkError') || errorMessage.includes('fetch') || errorMessage.includes('Failed to fetch')) {
      return '网络连接失败，请检查网络设置后重试';
    }
    
    // HTTP状态码错误 - 使用通用消息
    if (errorMessage.includes('HTTP error! status: 400') || errorMessage.includes('HTTP error! status: 401')) {
      return '用户名或密码错误，请检查后重试';
    }
    if (errorMessage.includes('HTTP error! status: 403')) {
      return '账户已被禁用，请联系管理员';
    }
    if (errorMessage.includes('HTTP error! status: 404') || errorMessage.includes('HTTP error! status: 502') || errorMessage.includes('HTTP error! status: 503')) {
      return '服务暂时不可用，请稍后重试';
    }
    if (errorMessage.includes('HTTP error! status: 500')) {
      return '服务器错误，请稍后重试';
    }
    
    // 登录相关错误
    if (errorMessage.includes('Invalid credentials') || errorMessage.includes('用户名或密码错误')) {
      return '用户名或密码错误，请检查后重试';
    }
    if (errorMessage.includes('Account locked') || errorMessage.includes('账户被锁定')) {
      return '账户已被锁定，请联系管理员';
    }
    if (errorMessage.includes('Account disabled') || errorMessage.includes('账户被禁用')) {
      return '账户已被禁用，请联系管理员';
    }
    
    // 通用错误消息
    if (errorMessage.includes('登录失败')) {
      return '登录失败，请稍后重试';
    }
    if (errorMessage.includes('timeout')) {
      return '请求超时，请检查网络连接后重试';
    }
    
    // 如果无法识别的错误，返回通用消息（不泄露系统信息）
    if (!isProduction) {
      logger.warn('未识别的登录错误消息:', errorMessage);
    }
    return '登录失败，请稍后重试';
  }

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await ApiClient.post<ApiResponse<LoginResponse>>('/users/login', credentials);
      
      // 检查响应是否成功
      if (!response.success) {
        throw new Error(response.message || '登录失败');
      }
      
      const data = response.data;
      
      // 存储到localStorage时，将id转换为字符串以保持一致性
      const userToStore = {
        ...data.user,
        id: data.user.id.toString(), // 转换为字符串存储
        email: data.user.email || '' // 如果email为空，使用空字符串
      };
      
      localStorage.setItem('token', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('tokenExpiresAt', String(Date.now() + data.expiresIn * 1000));
      localStorage.setItem('user', JSON.stringify(userToStore));
      
      // 清除登出标志，允许处理 storage 事件
      try {
        sessionStorage.removeItem('wavelink_logged_out');
      } catch (error) {
        console.warn('清除登出标志失败:', error);
      }
      
      // 触发登录成功事件
      window.dispatchEvent(new CustomEvent('auth-success'));
      
      return data;
    } catch (error) {
      logger.error('Login error:', error);
      // 转换为用户友好的错误消息
      const userFriendlyError = this.getLoginFriendlyErrorMessage(error);
      throw new Error(userFriendlyError);
    }
  }

  async logout(): Promise<void> {
    try {
      const token = this.getToken();
      
      // 只有在token存在时才尝试调用后端登出API
      if (token) {
        // 注意：退出登录时不调用 stopListeningDevice()，因为这会删除后端的连接记录
        // 退出登录只清理前端状态，连接记录应该由用户主动"断开设备"操作来删除
        // 这样用户重新登录后，仍然可以看到之前的设备
        
        try {
          const response = await ApiClient.post<ApiResponse<string>>('/users/logout', null, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          
          // 检查响应是否成功
          if (!response.success) {
            // 如果是token无效的错误，这是正常的，不需要警告
            if (response.message && response.message.includes('无效的token')) {
              logger.debug('登出成功（token已过期，本地清理完成）');
            } else {
              logger.warn('登出响应警告:', response.message);
            }
          } else {
            logger.debug('登出成功');
          }
        } catch (error) {
          // 如果是网络错误或token验证失败，这是正常的
          logger.debug('登出完成（本地清理）');
        }
      } else {
        logger.debug('无有效token，直接清理本地数据');
      }
    } catch (error) {
      logger.error('登出过程中发生错误:', error);
    } finally {
      // 首先设置登出标志，阻止任何后续的MQTT连接尝试
      // 使用 sessionStorage，因为它不会跨标签页/设备同步
      // 这必须在任何清理操作之前完成，以防止清理过程中触发的 useEffect 重新连接
      try {
        sessionStorage.setItem('wavelink_logged_out', 'true');
        logger.debug('已设置登出标志（阻止后续MQTT连接）');
      } catch (error) {
        logger.warn('设置登出标志失败:', error);
      }

      // 触发登出开始事件，让组件有机会在token失效前进行清理
      logger.debug('通知组件开始登出清理...');
      window.dispatchEvent(new CustomEvent('auth-logout-start'));
      
      // 等待一小段时间让组件完成清理
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // 执行完整的资源清理
      try {
        logger.debug('开始执行完整的资源清理...');
        await logoutCleanupService.performLogoutCleanup({
          force: true, // 强制清理，忽略错误
          silent: false // 输出清理日志
        });
        logger.debug('资源清理完成');
      } catch (error) {
        logger.error('资源清理失败:', error);
        // 即使清理失败，也要继续登出流程
      }
      
      // 清理本地存储
      try {
        this.clearAuth();
        logger.debug('本地存储清理完成');
      } catch (error) {
        logger.error('清理本地存储失败:', error);
      }
      
      // 最后触发登出完成事件，通知UI更新状态
      window.dispatchEvent(new CustomEvent('auth-logout'));
    }
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  /**
   * 检查token是否即将过期（在5分钟内过期）
   */
  isTokenExpiringSoon(): boolean {
    const expiresAt = localStorage.getItem('tokenExpiresAt');
    if (!expiresAt) {
      return true; // 如果没有过期时间，认为即将过期
    }
    const expiresAtTime = parseInt(expiresAt, 10);
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000; // 5分钟
    return expiresAtTime - now < fiveMinutes;
  }

  /**
   * 检查token是否已过期
   */
  isTokenExpired(): boolean {
    const expiresAt = localStorage.getItem('tokenExpiresAt');
    if (!expiresAt) {
      return true; // 如果没有过期时间，认为已过期
    }
    const expiresAtTime = parseInt(expiresAt, 10);
    return Date.now() >= expiresAtTime;
  }

  /**
   * 刷新token
   */
  async refreshToken(): Promise<string> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('没有可用的refresh token');
    }

    try {
      const response = await ApiClient.post<ApiResponse<RefreshTokenResponse>>('/users/refresh', {
        refreshToken
      });

      if (!response.success) {
        throw new Error(response.message || 'Token刷新失败');
      }

      const data = response.data;
      
      // 更新token和过期时间
      localStorage.setItem('token', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('tokenExpiresAt', String(Date.now() + data.expiresIn * 1000));

      // 触发token刷新事件
      window.dispatchEvent(new CustomEvent('token-refreshed'));

      return data.accessToken;
    } catch (error) {
      logger.error('Token刷新失败:', error);
      // 如果刷新失败，清除所有认证信息
      this.clearAuth();
      throw error;
    }
  }

  /**
   * 清除所有认证信息
   */
  private clearAuth(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenExpiresAt');
    localStorage.removeItem('user');
  }

  /**
   * 获取有效的token，如果即将过期则自动刷新
   */
  async getValidToken(): Promise<string | null> {
    // 如果token已过期，尝试刷新
    if (this.isTokenExpired()) {
      try {
        const newToken = await this.refreshToken();
        return newToken;
      } catch (error) {
        logger.error('获取有效token失败:', error);
        return null;
      }
    }

    // 如果token即将过期，尝试刷新
    if (this.isTokenExpiringSoon()) {
      try {
        const newToken = await this.refreshToken();
        return newToken;
      } catch (error) {
        logger.warn('Token刷新失败，使用当前token:', error);
        // 如果刷新失败，返回当前token（可能还能用一会儿）
        return this.getToken();
      }
    }

    return this.getToken();
  }

  getUser(): User | null {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return null;
      
      const storedUser = JSON.parse(userStr);
      
      // 验证用户对象的结构，email字段可以为空
      if (!storedUser || !storedUser.id || !storedUser.username) {
        logger.warn('用户信息格式不正确，清除无效数据');
        localStorage.removeItem('user');
        return null;
      }
      
      // 转换为与后端一致的User类型
      const user: User = {
        ...storedUser,
        id: parseInt(storedUser.id, 10), // 将字符串ID转换为数字
        email: storedUser.email || undefined // 如果为空字符串，转换为undefined
      };
      
      return user;
    } catch (error) {
      logger.error('解析用户信息失败:', error);
      localStorage.removeItem('user');
      return null;
    }
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  /**
   * 将技术错误消息转换为用户友好的消息
   * 生产环境不泄露系统内部信息
   */
  private getUserFriendlyErrorMessage(error: any): string {
    const isProduction = process.env.NODE_ENV === 'production';
    const errorMessage = error instanceof Error ? error.message : String(error);
    
    // 在生产环境中，记录详细错误但不返回给用户
    if (isProduction) {
      logger.error('注册错误详情（仅记录）:', {
        message: errorMessage,
        type: error?.constructor?.name,
        // 不记录堆栈跟踪和敏感信息
      });
    }
    
    // 网络错误
    if (errorMessage.includes('NetworkError') || errorMessage.includes('fetch') || errorMessage.includes('Failed to fetch')) {
      return '网络连接失败，请检查网络设置后重试';
    }
    
    // HTTP状态码错误 - 使用通用消息
    if (errorMessage.includes('HTTP error! status: 400')) {
      return '请求参数错误，请检查输入信息';
    }
    if (errorMessage.includes('HTTP error! status: 401')) {
      return '认证失败，请重新登录';
    }
    if (errorMessage.includes('HTTP error! status: 403')) {
      return '权限不足，无法执行此操作';
    }
    if (errorMessage.includes('HTTP error! status: 404') || errorMessage.includes('HTTP error! status: 502') || errorMessage.includes('HTTP error! status: 503')) {
      return '服务暂时不可用，请稍后重试';
    }
    if (errorMessage.includes('HTTP error! status: 500')) {
      return '服务器错误，请稍后重试';
    }
    
    // 用户名相关错误
    if (errorMessage.includes('username') && errorMessage.includes('already exists')) {
      return '用户名已存在，请选择其他用户名';
    }
    if (errorMessage.includes('username') && errorMessage.includes('invalid')) {
      return '用户名格式不正确，请使用3-32个字符';
    }
    
    // 邮箱相关错误
    if (errorMessage.includes('email') && errorMessage.includes('already exists')) {
      return '邮箱已被注册，请使用其他邮箱';
    }
    if (errorMessage.includes('email') && errorMessage.includes('invalid')) {
      return '邮箱格式不正确，请输入有效的邮箱地址';
    }
    
    // 密码相关错误
    if (errorMessage.includes('password') && errorMessage.includes('too short')) {
      return '密码长度不足，请至少使用6个字符';
    }
    if (errorMessage.includes('password') && errorMessage.includes('too weak')) {
      return '密码强度不足，请使用更复杂的密码';
    }
    
    // 通用错误消息
    if (errorMessage.includes('Registration failed')) {
      return '注册失败，请稍后重试';
    }
    if (errorMessage.includes('timeout')) {
      return '请求超时，请检查网络连接后重试';
    }
    
    // 如果无法识别的错误，返回通用消息（不泄露系统信息）
    if (!isProduction) {
      logger.warn('未识别的错误消息:', errorMessage);
    }
    return '注册失败，请稍后重试';
  }

  async register(userData: RegisterRequest): Promise<void> {
    try {
      logger.debug('注册请求数据:', userData);
      
      const response = await ApiClient.post<ApiResponse<{ success: boolean; message?: string }>>('/users/register', userData);
      logger.debug('注册响应:', response);
      
      // 检查后端返回的成功状态
      if (!response.success) {
        throw new Error(response.message || 'Registration failed');
      }
      
      logger.debug('注册成功');
    } catch (error) {
      logger.error('Registration error:', error);
      // 转换为用户友好的错误消息
      const userFriendlyError = this.getUserFriendlyErrorMessage(error);
      throw new Error(userFriendlyError);
    }
  }


}

export const authService = new AuthService(); 